// Panel compacto de Maya dentro del hero V2. Reutiliza el archivo aprobado
// del personaje y la muestra de audio existente (20–30 s). La guía completa y
// su descarga siguen bloqueadas por el sistema seguro actual hasta que exista
// una reserva confirmada y pagada.
import { useTranslation } from "react-i18next";
import { Lock, X, Headphones } from "lucide-react";

import { MAYA_IMAGE_URL } from "@/lib/maya-art";
import { MayaAudioPreview } from "@/components/maya/MayaAudioPreview";
import { normalizeLang } from "@/i18n";

export interface TourV2MayaPanelProps {
  destinationKey: string;
  destinationName: string;
  location: string | null;
  summary: string | null;
  lang: string;
  /** true solo cuando el sistema seguro confirma reserva pagada. */
  unlocked?: boolean;
  onClose: () => void;
}

export function TourV2MayaPanel(props: TourV2MayaPanelProps) {
  const { t, i18n } = useTranslation();
  // Maya hereda el idioma global del encabezado; no hay segundo selector.
  const base = normalizeLang(i18n.language || props.lang);
  // La muestra de audio existe en es/en/fr/pt; el resto escucha español.
  const lang = (["es", "en", "fr", "pt"] as const).includes(base as "es")
    ? (base as "es" | "en" | "fr" | "pt")
    : "es";

  return (
    <section
      aria-label="Maya IA"
      className="rounded-3xl border border-border/70 bg-card p-5 shadow-[0_2px_10px_rgba(23,23,23,0.05)]"
    >
      <div className="flex items-start gap-3">
        <img
          src={MAYA_IMAGE_URL}
          alt={t("tourV2.mayaAlt", { defaultValue: "Maya, tu guía digital" }) as string}
          width={56}
          height={56}
          loading="lazy"
          decoding="async"
          className="h-14 w-14 shrink-0 object-contain"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold">Maya IA</p>
          <p className="text-xs text-muted-foreground">
            {t("tourV2.mayaSubtitle", { defaultValue: "Tu guía digital para este recorrido" })}
          </p>
        </div>
        <button
          type="button"
          onClick={props.onClose}
          aria-label={t("tourV2.mayaClose", { defaultValue: "Volver al calendario" }) as string}
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-border text-foreground transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="mt-4">
        <MayaAudioPreview
          destinationKey={props.destinationKey}
          destinationName={props.destinationName}
          location={props.location}
          summary={props.summary}
          lang={lang}
        />
      </div>

      <div className="mt-4 rounded-2xl bg-muted/50 p-3">
        <p className="inline-flex items-center gap-1.5 text-xs font-semibold">
          {props.unlocked ? (
            <Headphones className="h-4 w-4 text-success" />
          ) : (
            <Lock className="h-4 w-4 text-muted-foreground" />
          )}
          {t("tourV2.mayaGuide", { defaultValue: "Guía digital" })}
        </p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {props.unlocked
            ? t("tourV2.mayaUnlocked", {
                defaultValue: "Tu guía completa está disponible en tu reserva confirmada.",
              })
            : t("tourV2.mayaLocked", {
                defaultValue:
                  "La guía completa y su descarga se activan al confirmarse tu reserva pagada.",
              })}
        </p>
      </div>
    </section>
  );
}
